import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, ArrowUp, ArrowDown, RotateCcw, RefreshCw, FolderPlus,
  GripVertical, ChevronDown, ChevronUp, ArrowRight,
} from "lucide-react";

export default function AdminMenuConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/menu/config");
      setConfig(data.config);
      setOriginal(JSON.stringify(data.config));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/menu/config", config);
      setConfig(data.config);
      setOriginal(JSON.stringify(data.config));
      toast.success(data.message || "Menu layout saved — sidebar updates on next page load");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset both admin and partner menus to built-in defaults?")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/menu/config/reset");
      setConfig(data.config);
      setOriginal(JSON.stringify(data.config));
      toast.success("Reset to defaults");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = config && JSON.stringify(config) !== original;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="admin-menu-config-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="overline">Admin · System</span>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-2">Menu layout</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
              Organise the sidebar into collapsible groups and change the order of items.
              Changes apply to <b>all users</b> on next page load.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={reset} disabled={saving} data-testid="reset-menu">
              <RotateCcw className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Reset defaults
            </Button>
            <Button size="sm" onClick={save} disabled={saving || !hasChanges} data-testid="save-menu">
              {saving ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" strokeWidth={1.5} /> : <Save className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />}
              {saving ? "Saving…" : hasChanges ? "Save" : "Saved"}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="mt-8 flex items-center gap-2 text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : config && (
          <Tabs defaultValue="admin" className="mt-6" data-testid="menu-tabs">
            <TabsList>
              <TabsTrigger value="admin" data-testid="tab-admin-menu">Admin sidebar</TabsTrigger>
              <TabsTrigger value="partner" data-testid="tab-partner-menu">Partner sidebar</TabsTrigger>
            </TabsList>
            <TabsContent value="admin" className="mt-4">
              <MenuEditor
                entries={config.admin || []}
                onChange={(admin) => setConfig({ ...config, admin })}
                side="admin"
              />
            </TabsContent>
            <TabsContent value="partner" className="mt-4">
              <MenuEditor
                entries={config.partner || []}
                onChange={(partner) => setConfig({ ...config, partner })}
                side="partner"
              />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}

/* ─── Menu editor (one per side) ──────────────────────────────── */

function MenuEditor({ entries, onChange, side }) {
  const move = (idx, dir) => {
    const next = [...entries];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  const removeEntry = (idx) => {
    onChange(entries.filter((_, i) => i !== idx));
  };

  const addGroup = () => {
    const id = `grp_new_${Date.now().toString(36)}`;
    onChange([...entries, { type: "group", id, label: "New group", items: [] }]);
  };

  const updateGroup = (idx, key, value) => {
    onChange(entries.map((e, i) => (i === idx ? { ...e, [key]: value } : e)));
  };

  // Move an item within a group
  const moveItem = (groupIdx, itemIdx, dir) => {
    const group = { ...entries[groupIdx], items: [...entries[groupIdx].items] };
    const j = itemIdx + dir;
    if (j < 0 || j >= group.items.length) return;
    [group.items[itemIdx], group.items[j]] = [group.items[j], group.items[itemIdx]];
    onChange(entries.map((e, i) => (i === groupIdx ? group : e)));
  };

  const removeItem = (groupIdx, itemIdx) => {
    const group = { ...entries[groupIdx], items: entries[groupIdx].items.filter((_, i) => i !== itemIdx) };
    onChange(entries.map((e, i) => (i === groupIdx ? group : e)));
  };

  // Move item to a different group
  const moveItemToGroup = (fromGroupIdx, itemIdx, toGroupIdx) => {
    const fromGroup = { ...entries[fromGroupIdx], items: [...entries[fromGroupIdx].items] };
    const [item] = fromGroup.items.splice(itemIdx, 1);
    const toGroup = { ...entries[toGroupIdx], items: [...entries[toGroupIdx].items, item] };
    onChange(entries.map((e, i) => {
      if (i === fromGroupIdx) return fromGroup;
      if (i === toGroupIdx) return toGroup;
      return e;
    }));
  };

  // Add ungrouped item
  const addItem = () => {
    const id = `item_new_${Date.now().toString(36)}`;
    onChange([...entries, {
      type: "item", id, to: "/", label: "New item", icon: "Zap",
    }]);
  };

  const updateTopLevelItem = (idx, key, value) => {
    onChange(entries.map((e, i) => (i === idx ? { ...e, [key]: value } : e)));
  };

  const groupIndices = entries.map((e, i) => (e.type === "group" ? i : -1)).filter((i) => i >= 0);

  return (
    <div className="space-y-3" data-testid={`menu-editor-${side}`}>
      {entries.map((entry, idx) => {
        if (entry.type === "group") {
          return (
            <GroupCard
              key={entry.id}
              group={entry}
              idx={idx}
              total={entries.length}
              onMove={(dir) => move(idx, dir)}
              onRemove={() => removeEntry(idx)}
              onUpdateLabel={(label) => updateGroup(idx, "label", label)}
              onMoveItem={(itemIdx, dir) => moveItem(idx, itemIdx, dir)}
              onRemoveItem={(itemIdx) => removeItem(idx, itemIdx)}
              onMoveItemToGroup={(itemIdx, toGroupIdx) => moveItemToGroup(idx, itemIdx, toGroupIdx)}
              groupIndices={groupIndices}
              allEntries={entries}
              side={side}
            />
          );
        }
        // Top-level ungrouped item
        return (
          <ItemRow
            key={entry.id || idx}
            item={entry}
            idx={idx}
            total={entries.length}
            onMove={(dir) => move(idx, dir)}
            onRemove={() => removeEntry(idx)}
            onUpdate={(key, val) => updateTopLevelItem(idx, key, val)}
            groupIndices={groupIndices}
            allEntries={entries}
            onMoveToGroup={(toGroupIdx) => {
              const item = entries[idx];
              const toGroup = { ...entries[toGroupIdx], items: [...entries[toGroupIdx].items, item] };
              onChange(entries.filter((_, i) => i !== idx).map((e, i) => {
                const realIdx = entries.indexOf(e);
                return realIdx === toGroupIdx ? toGroup : e;
              }));
            }}
            isTopLevel
          />
        );
      })}

      <div className="flex gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={addGroup} data-testid={`add-group-${side}`}>
          <FolderPlus className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Add group
        </Button>
        <Button variant="outline" size="sm" onClick={addItem} data-testid={`add-item-${side}`}>
          <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Add ungrouped item
        </Button>
      </div>
    </div>
  );
}

/* ─── Group card ──────────────────────────────────────────────── */

function GroupCard({ group, idx, total, onMove, onRemove, onUpdateLabel,
  onMoveItem, onRemoveItem, onMoveItemToGroup, groupIndices, allEntries, side }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="rounded-lg border border-border bg-card" data-testid={`group-card-${group.id}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/30 rounded-t-lg">
        <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
        <button type="button" onClick={() => setExpanded((s) => !s)} className="shrink-0">
          {expanded ? <ChevronUp className="h-4 w-4" strokeWidth={1.5} /> : <ChevronDown className="h-4 w-4" strokeWidth={1.5} />}
        </button>
        <Input
          value={group.label}
          onChange={(e) => onUpdateLabel(e.target.value)}
          className="h-7 text-sm font-semibold border-none bg-transparent shadow-none focus-visible:ring-0 px-1 flex-1"
          data-testid={`group-label-${group.id}`}
        />
        <span className="text-[10px] text-muted-foreground font-mono shrink-0">{(group.items || []).length} items</span>
        <div className="flex items-center gap-0.5 shrink-0">
          <button type="button" onClick={() => onMove(-1)} disabled={idx === 0} className="p-1 rounded hover:bg-accent disabled:opacity-30" data-testid={`group-up-${group.id}`}>
            <ArrowUp className="h-3 w-3" strokeWidth={1.5} />
          </button>
          <button type="button" onClick={() => onMove(1)} disabled={idx === total - 1} className="p-1 rounded hover:bg-accent disabled:opacity-30" data-testid={`group-down-${group.id}`}>
            <ArrowDown className="h-3 w-3" strokeWidth={1.5} />
          </button>
          <button type="button" onClick={onRemove} className="p-1 rounded hover:bg-destructive/10 text-destructive" data-testid={`group-remove-${group.id}`}>
            <Trash2 className="h-3 w-3" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="p-2 space-y-1">
          {(group.items || []).length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-3">
              Empty group — drag items here or delete this group.
            </div>
          )}
          {(group.items || []).map((item, iIdx) => (
            <ItemRow
              key={item.id || iIdx}
              item={item}
              idx={iIdx}
              total={(group.items || []).length}
              onMove={(dir) => onMoveItem(iIdx, dir)}
              onRemove={() => onRemoveItem(iIdx)}
              groupIndices={groupIndices}
              allEntries={allEntries}
              currentGroupIdx={idx}
              onMoveToGroup={(toGroupIdx) => onMoveItemToGroup(iIdx, toGroupIdx)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Single item row ─────────────────────────────────────────── */

function ItemRow({ item, idx, total, onMove, onRemove, onUpdate,
  groupIndices, allEntries, currentGroupIdx, onMoveToGroup, isTopLevel }) {
  const otherGroups = (groupIndices || [])
    .filter((gi) => gi !== currentGroupIdx)
    .map((gi) => ({ idx: gi, label: allEntries[gi]?.label || `Group ${gi}` }));

  return (
    <div
      className="flex items-center gap-2 px-2 py-1.5 rounded border border-border/50 bg-background hover:bg-accent/20 transition-colors"
      data-testid={`menu-item-${item.id}`}
    >
      <GripVertical className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" strokeWidth={1.5} />
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="text-xs font-mono text-muted-foreground shrink-0 w-5 text-right">{idx + 1}.</span>
        {isTopLevel && onUpdate ? (
          <>
            <Input
              value={item.label || ""}
              onChange={(e) => onUpdate("label", e.target.value)}
              className="h-6 text-xs border-none bg-transparent shadow-none focus-visible:ring-0 px-1 flex-1 min-w-[80px]"
            />
            <Input
              value={item.to || ""}
              onChange={(e) => onUpdate("to", e.target.value)}
              className="h-6 text-[10px] font-mono border-none bg-transparent shadow-none focus-visible:ring-0 px-1 w-32 text-muted-foreground"
              placeholder="/path"
            />
          </>
        ) : (
          <>
            <span className="text-sm truncate">{item.label}</span>
            <span className="text-[10px] font-mono text-muted-foreground truncate">{item.to}</span>
          </>
        )}
        {item.featureKey && (
          <span className="text-[9px] font-mono text-amber-600 bg-amber-50 px-1 rounded shrink-0">{item.featureKey}</span>
        )}
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        {otherGroups.length > 0 && (
          <select
            className="h-6 text-[10px] rounded border border-border bg-background px-1 max-w-[90px]"
            value=""
            onChange={(e) => {
              const toIdx = parseInt(e.target.value, 10);
              if (!isNaN(toIdx)) onMoveToGroup(toIdx);
            }}
            title="Move to group"
            data-testid={`move-item-${item.id}`}
          >
            <option value="">Move to…</option>
            {otherGroups.map((g) => (
              <option key={g.idx} value={g.idx}>{g.label}</option>
            ))}
          </select>
        )}
        <button type="button" onClick={() => onMove(-1)} disabled={idx === 0} className="p-0.5 rounded hover:bg-accent disabled:opacity-30">
          <ArrowUp className="h-3 w-3" strokeWidth={1.5} />
        </button>
        <button type="button" onClick={() => onMove(1)} disabled={idx === total - 1} className="p-0.5 rounded hover:bg-accent disabled:opacity-30">
          <ArrowDown className="h-3 w-3" strokeWidth={1.5} />
        </button>
        <button type="button" onClick={onRemove} className="p-0.5 rounded hover:bg-destructive/10 text-destructive">
          <Trash2 className="h-3 w-3" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
